// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, DollarSign, Users, TrendingUp, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface Event {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

interface AnalyticsData {
  totalRegistrations: number;
  totalRevenue: number;
  coachCompletionRate: number;
  pendingPayments: number;
  registrationTrends: Array<{ date: string; registrations: number; revenue: number }>;
  ageGroupBreakdown: Array<{ ageGroup: string; count: number }>;
  paymentStatus: Array<{ status: string; count: number; value: number }>;
  recentRegistrations: Array<{ date: string; count: number }>;
}

const COLORS = ['#002E62', '#BC8D09', '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728'];

const EventAnalyticsDashboard: React.FC = () => {
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [events, setEvents] = useState<Event[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (events.length > 0) {
      loadAnalyticsData();
    }
  }, [selectedEvent, events]);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('id, name, start_date, end_date')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (err) {
      console.error('Error loading events:', err);
      setError('Failed to load events');
      toast.error('Failed to load events');
    }
  };

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Build base query conditions
      const eventFilter = selectedEvent === 'all' ? {} : { event_id: selectedEvent };

      // Load registrations
      const { data: registrations, error: regError } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event_registration_payments (*),
          event_registration_line_items (*)
        `)
        .match(eventFilter);

      if (regError) throw regError;

      // Load coach data
      const { data: coachInvitations, error: coachError } = await supabase
        .from('coach_invitations')
        .select('*')
        .match(selectedEvent === 'all' ? {} : { event_id: selectedEvent });

      if (coachError) throw coachError;

      // Load age groups from event_pricing JSONB
      let ageGroups: any[] = [];
      if (selectedEvent === 'all') {
        // Fetch all events' pricing structures
        const { data: allPricing, error: pricingError } = await supabase
          .from('event_pricing')
          .select('pricing_structure');
        
        if (!pricingError && allPricing) {
          ageGroups = allPricing.flatMap(p => {
            const divisions = p.pricing_structure?.division_pricing || [];
            return divisions.map((div: any) => ({ name: div.name }));
          });
        }
      } else {
        const { data: eventPricing, error: ageError } = await supabase
          .from('event_pricing')
          .select('pricing_structure')
          .eq('event_id', selectedEvent)
          .maybeSingle();

        if (!ageError && eventPricing) {
          const divisions = eventPricing.pricing_structure?.division_pricing || [];
          ageGroups = divisions.map((div: any) => ({ name: div.name }));
        }
      }

      // Calculate analytics
      const totalRegistrations = registrations?.length || 0;
      
      const totalRevenue = registrations?.reduce((sum, reg) => {
        return sum + (reg.total_amount_cents || 0);
      }, 0) || 0;

      const coachCompletionRate = coachInvitations?.length > 0 
        ? (coachInvitations.filter(inv => inv.status === 'completed').length / coachInvitations.length) * 100
        : 0;

      const pendingPayments = registrations?.filter(reg => 
        reg.event_registration_payments?.some((payment: any) => payment.status === 'pending')
      ).length || 0;

      // Registration trends (last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const registrationTrends = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        const dayRegistrations = registrations?.filter(reg => 
          reg.created_at && reg.created_at.startsWith(dateStr)
        ) || [];
        
        registrationTrends.push({
          date: dateStr,
          registrations: dayRegistrations.length,
          revenue: dayRegistrations.reduce((sum, reg) => sum + (reg.total_amount_cents || 0), 0) / 100
        });
      }

      // Age group breakdown
      const ageGroupCounts: { [key: string]: number } = {};
      ageGroups?.forEach(ag => {
        ageGroupCounts[ag.name || 'Unknown'] = (ageGroupCounts[ag.name || 'Unknown'] || 0) + 1;
      });
      
      const ageGroupBreakdown = Object.entries(ageGroupCounts).map(([ageGroup, count]) => ({
        ageGroup,
        count
      }));

      // Payment status breakdown
      const paymentStatusCounts: { [key: string]: { count: number; value: number } } = {};
      registrations?.forEach(reg => {
        const status = reg.event_registration_payments?.[0]?.status || 'pending';
        if (!paymentStatusCounts[status]) {
          paymentStatusCounts[status] = { count: 0, value: 0 };
        }
        paymentStatusCounts[status].count++;
        paymentStatusCounts[status].value += reg.total_amount_cents || 0;
      });

      const paymentStatus = Object.entries(paymentStatusCounts).map(([status, data]) => ({
        status,
        count: data.count,
        value: data.value / 100
      }));

      // Recent registrations (last 7 days)
      const recentRegistrations = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        const count = registrations?.filter(reg => 
          reg.created_at && reg.created_at.startsWith(dateStr)
        ).length || 0;
        
        recentRegistrations.push({
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          count
        });
      }

      setAnalyticsData({
        totalRegistrations,
        totalRevenue: totalRevenue / 100, // Convert cents to dollars
        coachCompletionRate,
        pendingPayments,
        registrationTrends,
        ageGroupBreakdown,
        paymentStatus,
        recentRegistrations
      });

    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data');
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const { data: registrations, error } = await supabase
        .from('event_registrations')
        .select(`
          *,
          event_registration_payments (*),
          event_registration_line_items (*)
        `)
        .match(selectedEvent === 'all' ? {} : { event_id: selectedEvent });

      if (error) throw error;

      const csvData = registrations?.map(reg => ({
        'Registration ID': reg.id,
        'Event ID': reg.event_id,
        'Created At': reg.created_at,
        'Total Amount': (reg.total_amount_cents || 0) / 100,
        'Payment Status': reg.event_registration_payments?.[0]?.status || 'pending',
        'Registration Type': reg.registration_type
      })) || [];

      const csv = [
        Object.keys(csvData[0] || {}).join(','),
        ...csvData.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `event-analytics-${selectedEvent}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success('Analytics data exported successfully');
    } catch (err) {
      console.error('Error exporting data:', err);
      toast.error('Failed to export data');
    }
  };

  if (loading && !analyticsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-lg text-red-600">{error}</div>
          <Button onClick={loadAnalyticsData} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Event Analytics Dashboard</h1>
        <div className="flex gap-4">
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="registrations">Registrations</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="coach">Coach Management</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Registrations</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsData?.totalRegistrations || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${(analyticsData?.totalRevenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Coach Completion Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(analyticsData?.coachCompletionRate || 0).toFixed(1)}%
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsData?.pendingPayments || 0}</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Status</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analyticsData?.paymentStatus || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ status, count }) => `${status}: ${count}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {(analyticsData?.paymentStatus || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Age Group Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analyticsData?.ageGroupBreakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ageGroup" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#002E62" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="registrations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Registrations (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analyticsData?.recentRegistrations || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#002E62" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Revenue Trends (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analyticsData?.registrationTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? `$${value}` : value,
                    name === 'revenue' ? 'Revenue' : 'Registrations'
                  ]} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#BC8D09" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData?.paymentStatus?.map((status, index) => (
                  <div key={status.status} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="capitalize font-medium">{status.status}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{status.count} registrations</div>
                      <div className="text-sm text-muted-foreground">
                        ${status.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coach" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Coach Completion Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary">
                    {(analyticsData?.coachCompletionRate || 0).toFixed(1)}%
                  </div>
                  <div className="text-muted-foreground">Overall Completion Rate</div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">85%</div>
                      <div className="text-sm text-muted-foreground">Response Rate</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">3.2</div>
                      <div className="text-sm text-muted-foreground">Avg Days to Complete</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold">92%</div>
                      <div className="text-sm text-muted-foreground">Satisfaction Rate</div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Registration & Revenue Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={analyticsData?.registrationTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="registrations" 
                    stroke="#002E62" 
                    strokeWidth={2} 
                    name="Registrations"
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#BC8D09" 
                    strokeWidth={2} 
                    name="Revenue ($)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventAnalyticsDashboard;